import { FC, useState } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Spacer,
  Surface,
  Text,
  TextInput,
} from "@zapier/design-system";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  zslCreatorBaseUrlAtom,
  generateZslSetter,
  generateZslErrorAtom,
  canvasNameAtom,
  makeImagesPublicSetter,
  isZapNode,
  isDefaultNode,
  isTextNode,
  isImageNode,
  edgesAtom,
  nodesAtom,
} from "@zapier/canvas-state";
import { zslToCanvasErrorAtom, zslToCanvasPostSetter } from "@zapier/streaming";
import styles from "./DevTools.module.css";
import { composeCanvasForTemplate } from "../../utils/composeCanvasForTemplate";
import { toKebabCase } from "../../../../service/src/app/utils/stringUtils";

export const ZSLTabContent: FC = () => {
  const canvasName = useAtomValue(canvasNameAtom);
  const [zslCreatorBaseUrl, setZslCreatorBaseUrl] = useAtom(
    zslCreatorBaseUrlAtom,
  );
  const [zslToCanvasError, setZslToCanvasError] = useAtom(zslToCanvasErrorAtom);

  const convertZslToCanvas = useSetAtom(zslToCanvasPostSetter);
  const [localZsl, setLocalZsl] = useState("");

  const generateZsl = useSetAtom(generateZslSetter);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [generateZslError, setGenerateZslError] = useAtom(generateZslErrorAtom);
  const [template, setTemplate] = useState<any>(null);
  const makeImagesPublic = useSetAtom(makeImagesPublicSetter);
  const nodes = useAtomValue(nodesAtom);
  const edges = useAtomValue(edgesAtom);

  const handleChangeZslToCanvas = (event: any) => {
    setLocalZsl(event.target.value);
  };

  const handleSubmitZslToCanvas = async () => {
    try {
      await convertZslToCanvas(localZsl);
    } catch (error) {
      console.error("Error converting ZSL to Canvas:", error);
    }
  };

  const handleSubmitExportCanvas = async () => {
    const assetIds = {
      zapIds: [] as string[],
      tableIds: [] as string[],
      chatbotIds: [] as string[],
      interfaceIds: [] as string[],
      agentIds: [] as string[],
    };

    nodes.forEach((node) => {
      if (
        isZapNode(node) &&
        node.data.zapId &&
        !assetIds.zapIds.includes(node.data.zapId)
      ) {
        assetIds.zapIds.push(node.data.zapId);
      } else if (
        isDefaultNode(node) &&
        node.data.tableId &&
        !assetIds.tableIds.includes(node.data.tableId)
      ) {
        assetIds.tableIds.push(node.data.tableId);
      } else if (
        isDefaultNode(node) &&
        node.data.chatbotId &&
        !assetIds.chatbotIds.includes(node.data.chatbotId)
      ) {
        assetIds.chatbotIds.push(node.data.chatbotId);
      } else if (
        isDefaultNode(node) &&
        node.data.projectId &&
        !assetIds.interfaceIds.includes(node.data.projectId)
      ) {
        assetIds.interfaceIds.push(node.data.projectId);
      } else if (
        isDefaultNode(node) &&
        node.data.agentId &&
        !assetIds.agentIds.includes(node.data.agentId)
      ) {
        assetIds.agentIds.push(node.data.agentId);
      }
    });

    // Collect all image IDs from nodes that aren't already public
    const imageIds: string[] = [];
    nodes.forEach((node) => {
      const extractImageIds = (content: any) => {
        if (
          content?.attrs?.src?.includes("/api/canvas/v1/canvases") &&
          !content.attrs.src.includes("/api/canvas/v1/canvases/public")
        ) {
          const imageId = content.attrs.src.split("/").pop();
          if (imageId) imageIds.push(imageId);
        }
        if (content.content) {
          content.content.forEach((item: any) => extractImageIds(item));
        }
      };

      if (isDefaultNode(node) && node.data.notesJson?.content) {
        node.data.notesJson.content.forEach(extractImageIds);
      }
      if (isTextNode(node) && node.data.label?.content) {
        node.data.label.content.forEach(extractImageIds);
      }
      if (isImageNode(node) && node.data.imgUrl) {
        if (
          node.data.imgUrl.includes("/api/canvas/v1/canvases") &&
          !node.data.imgUrl.includes("/public")
        ) {
          const imageId = node.data.imgUrl.split("/").pop();
          if (imageId && !imageIds.includes(imageId)) {
            imageIds.push(imageId);
          }
        }
      }
    });

    try {
      setIsGeneratingTemplate(true);

      // Only make images public if there are images to process
      let imageIdMap: Record<string, string> = {};
      if (imageIds.length > 0) {
        imageIdMap = await makeImagesPublic(imageIds);
      }

      // Create new nodes with public image URLs
      const nodesWithPublicImages = nodes.map((node) => {
        const newNode = JSON.parse(JSON.stringify(node)); // Deep clone the node to avoid mutations
        const updateContent = (content: any) => {
          if (content?.attrs?.src?.includes("/api/canvas/v1/canvases")) {
            const imageId = content.attrs.src.split("/").pop();
            if (imageId && imageIdMap[imageId]) {
              content.attrs.src = `/api/canvas/v1/canvases/public/images/${imageIdMap[imageId]}`;
            }
          }
          if (content.content) {
            content.content.forEach(updateContent);
          }
        };

        if (newNode.data.notesJson?.content) {
          newNode.data.notesJson.content.forEach(updateContent);
        }
        if (newNode.data.label?.content) {
          newNode.data.label.content.forEach(updateContent);
        }
        if (isImageNode(newNode) && newNode.data.imgUrl) {
          if (
            newNode.data.imgUrl.includes("/api/canvas/v1/canvases") &&
            !newNode.data.imgUrl.includes("/public")
          ) {
            const imageId = newNode.data.imgUrl.split("/").pop();
            if (imageId && imageIdMap[imageId]) {
              newNode.data.imgUrl = `/api/canvas/v1/canvases/public/images/${imageIdMap[imageId]}`;
            }
          }
        }

        return newNode;
      });

      const data = await generateZsl(assetIds);
      const template = data.template;
      template.redirectTo = "_CANVAS_1";
      template.name = canvasName || "New Template";
      template.canvases = [
        composeCanvasForTemplate(
          canvasName,
          nodesWithPublicImages,
          edges,
          data.resourceMap,
        ),
      ];
      setTemplate(template);
      setIsGeneratingTemplate(false);
    } catch (error) {
      console.error("Error exporting Canvas:", error);
      setIsGeneratingTemplate(false);
    }
  };

  const handleSubmitDownloadTemplate = () => {
    if (!template) return;

    const jsonContent = JSON.stringify(template, null, 2);
    const blob = new Blob([jsonContent], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.download = `${toKebabCase(canvasName) || "untitled"}-template.json`;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setTemplate(null);
  };
  return (
    <div className={styles.devtoolsSectionWrapper}>
      <Alert title="" status="info" variant="compact">
        <AlertDescription>
          <Text type="MinimalPrint3">
            Convert ZSL to Canvas and export the Canvas to a template.
          </Text>
        </AlertDescription>
      </Alert>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Convert ZSL to Canvas</Text>
          {zslToCanvasError && (
            <>
              <Alert
                title={zslToCanvasError}
                onDismiss={() => setZslToCanvasError(null)}
                status="error"
              />
              <Spacer height={15}></Spacer>
            </>
          )}
          <TextInput
            isMultiline={true}
            rows={20}
            onChange={handleChangeZslToCanvas}
            value={localZsl}
            isErrored={!!zslToCanvasError}
          />
          <div className={styles.devToolsContentWrapper}>
            <div className={styles.buttonWrapperAlignRight}>
              <Button size="small" onPress={handleSubmitZslToCanvas}>
                Render Canvas
              </Button>
            </div>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Export Canvas to Template</Text>
          {generateZslError && (
            <>
              <Alert
                title={generateZslError}
                onDismiss={() => setGenerateZslError(null)}
                status="error"
              />
              <Spacer height={15}></Spacer>
            </>
          )}
          <div className={styles.devToolsContentWrapperFlex}>
            {template ? (
              <Button size="small" onPress={handleSubmitDownloadTemplate}>
                Download Template
              </Button>
            ) : (
              <div className={styles.exportCanvasButton}>
                <Button
                  size="small"
                  onPress={handleSubmitExportCanvas}
                  isPending={isGeneratingTemplate}
                >
                  Export Canvas
                </Button>
              </div>
            )}
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Set ZSL Creator Base URL</Text>
          <TextInput
            value={zslCreatorBaseUrl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setZslCreatorBaseUrl(e.target.value)
            }
          />
        </div>
      </Surface>
    </div>
  );
};
